import { z } from 'zod';

const MENU_TYPES = ['INTERNAL', 'EXTERNAL', 'DIVIDER'] as const;

/** CreateMenu — POST /api/eraf/menus */
export const createMenuSchema = z
  .object({
    menuKey: z
      .string()
      .min(1, '메뉴 키를 입력해주세요')
      .max(64, '메뉴 키는 64자 이하여야 합니다')
      .regex(/^[a-z][a-z0-9_]*$/, '메뉴 키는 소문자로 시작하고 소문자·숫자·밑줄만 사용해야 합니다'),
    nameKo: z
      .string()
      .min(1, '한글 이름을 입력해주세요')
      .max(128, '한글 이름은 128자 이하여야 합니다'),
    nameEn: z.string().max(128, '영문 이름은 128자 이하여야 합니다').optional(),
    labelKey: z.string().max(128).optional(),
    url: z.string().max(512).optional(),
    icon: z.string().max(64).optional(),
    menuType: z.enum(MENU_TYPES, { required_error: '메뉴 타입을 선택해주세요' }),
    parentId: z.number().nullable().optional(),
    sortOrder: z
      .number({ invalid_type_error: '순서를 숫자로 입력해주세요' })
      .int('정수로 입력해주세요')
      .min(0, '0 이상이어야 합니다')
      .max(99999, '99999 이하여야 합니다')
      .optional(),
    visible: z.boolean().optional(),
    requiredRole: z.string().max(64).optional(),
    /**
     * #471 — 화면-레벨 권한 코드 (cmn_security_permission.code의 logical FK).
     * 빈 문자열 → null 로 변환하여 "없음" 선택을 표현.
     */
    requiredPermission: z.string().max(64).nullable().optional(),
    description: z.string().max(512).optional(),
  })
  .refine(
    (data) => {
      if (data.menuType === 'DIVIDER') return true;
      return !!data.url;
    },
    {
      message: 'INTERNAL/EXTERNAL 메뉴는 URL이 필요합니다',
      path: ['url'],
    }
  );

export type CreateMenuFormValues = z.infer<typeof createMenuSchema>;

/** UpdateMenu — PATCH /api/eraf/menus/{id} */
export const updateMenuSchema = z.object({
  nameKo: z.string().min(1, '한글 이름을 입력해주세요').max(128).optional(),
  nameEn: z.string().max(128).optional(),
  labelKey: z.string().max(128).optional(),
  url: z.string().max(512).optional(),
  icon: z.string().max(64).optional(),
  menuType: z.enum(MENU_TYPES).optional(),
  parentId: z.number().nullable().optional(),
  sortOrder: z
    .number({ invalid_type_error: '순서를 숫자로 입력해주세요' })
    .int()
    .min(0)
    .max(99999)
    .optional(),
  visible: z.boolean().optional(),
  requiredRole: z.string().max(64).optional(),
  /**
   * #471 — 화면-레벨 권한 코드.
   * 빈 문자열 → null 로 변환 ("없음" 선택 = clear).
   */
  requiredPermission: z.string().max(64).nullable().optional(),
  description: z.string().max(512).optional(),
});

export type UpdateMenuFormValues = z.infer<typeof updateMenuSchema>;

/** MenuOrder — PATCH /api/eraf/menus/order */
export const menuOrderSchema = z.object({
  parentId: z.number().nullable().optional(),
  items: z
    .array(
      z.object({
        id: z.number(),
        sortOrder: z.number().int().min(0).max(99999),
      })
    )
    .min(1, '최소 1개 이상 필요합니다')
    .max(100, '최대 100개까지 가능합니다'),
});

export type MenuOrderFormValues = z.infer<typeof menuOrderSchema>;
